import { useState } from "react";
import { useTranslation } from "react-i18next";
import api, { apiErr, fmtTimeLong } from "../../lib/api";
import { Card } from "../ui-bits";
import { toast } from "sonner";
import { History, CheckCircle2, Download, Trash2, Loader2, Film } from "lucide-react";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "../ui/alert-dialog";

export default function RenderHistory({ project, refreshProject }) {
  const { t, i18n } = useTranslation();
  const history = project?.render_history || [];
  const activeId = project?.render?.output?.id;
  const [busyId, setBusyId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  if (history.length === 0) return null;

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString(i18n.language === "en" ? "en-US" : "es-ES",
        { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch (e) { return iso; }
  };

  const activate = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/projects/${project.id}/render/history/${id}/activate`);
      await refreshProject?.();
      toast.success(t("render.historyActivated"));
    } catch (e) { toast.error(apiErr(e)); } finally { setBusyId(null); }
  };

  const doDelete = async (id) => {
    setConfirmId(null);
    setBusyId(id);
    try {
      await api.delete(`/projects/${project.id}/render/history/${id}`);
      await refreshProject?.();
      toast.success(t("render.historyDeleted"));
    } catch (e) { toast.error(apiErr(e)); } finally { setBusyId(null); }
  };

  return (
    <Card className="p-6" data-testid="render-history">
      <div className="flex items-center gap-2 mb-1">
        <History className="text-blue-600" size={24} />
        <h3 className="text-xl font-bold font-head text-slate-900">{t("render.historyTitle")}</h3>
      </div>
      <p className="text-sm text-slate-500 mb-4">{t("render.historySub")}</p>

      <div className="space-y-3">
        {history.map((r) => {
          const active = r.id === activeId;
          const busy = busyId === r.id;
          return (
            <div key={r.id} data-testid={`render-version-${r.id}`}
              className={`flex items-center gap-4 rounded-xl border-2 p-3 transition-colors ${active ? "border-blue-500 bg-blue-50/40" : "border-slate-200 bg-white"}`}>
              <div className="w-28 aspect-video rounded-lg overflow-hidden bg-slate-800 shrink-0 flex items-center justify-center">
                {r.thumbnail ? <img src={r.thumbnail} alt="" className="w-full h-full object-cover" /> : <Film size={22} className="text-slate-400" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-base font-semibold text-slate-900">{fmtDate(r.created_at)}</span>
                  {active && (
                    <span data-testid={`render-version-active-${r.id}`} className="inline-flex items-center gap-1 bg-blue-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                      <CheckCircle2 size={12} /> {t("render.historyActive")}
                    </span>
                  )}
                </div>
                <div className="text-sm text-slate-500 mt-0.5">{fmtTimeLong(r.duration)} · {r.size_mb} MB · 1920×1080</div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {!active && (
                  <button data-testid={`render-version-use-${r.id}`} onClick={() => activate(r.id)} disabled={busy}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-60">
                    {busy ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle2 size={16} />}
                    <span className="hidden sm:inline">{t("render.historyUse")}</span>
                  </button>
                )}
                <a data-testid={`render-version-download-${r.id}`} href={r.video_url} download target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border-2 border-slate-200 text-slate-700 text-sm font-semibold hover:border-slate-300" title={t("render.historyDownload")}>
                  <Download size={16} />
                </a>
                <button data-testid={`render-version-delete-${r.id}`} onClick={() => setConfirmId(r.id)} disabled={busy}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border-2 border-slate-200 text-slate-500 hover:text-red-600 hover:border-red-300 text-sm font-semibold disabled:opacity-60" title={t("render.historyDelete")}>
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent data-testid="render-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("render.historyDelete")}</AlertDialogTitle>
            <AlertDialogDescription>{t("render.historyDeleteConfirm")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="render-delete-cancel">{t("render.cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="render-delete-accept" onClick={() => doDelete(confirmId)} className="bg-red-600 hover:bg-red-700">
              {t("render.historyDelete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
